import React, { useContext, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { getDateFromDateString, getTimeFromDateString } from "../../../Helpers/DateTimeHelper";
import { intervalFromState } from "../../../Helpers/IntervalHelper";
import { differenceInMinutes } from "date-fns";
import * as Yup from "yup";
import { BOOKINGS_API_URL } from "../../../Modules/Bookings/Bookings";
import { getErrorsFor } from "../../../Helpers/ErrorHelper";

export const recurrencePrototype = {
    id: '',
    repeats: null,
    start_date: '',
    time: '',
    duration: '',
    end_date: '',
    end_occurrences: '',
    every: '',
    on_type: null,
    on: '',
}

const recurrenceValidationSchema = Yup.object().shape({
    id: Yup.string().nullable(),
    repeats: Yup.string()
        .typeError('You must set a repeats value')
        .required('You must set a repeats value'),
    start_date: Yup.string().required(),
    time: Yup.string().required(),
    end_date: Yup.string()
        .typeError('You must set an end date')
        .required('You must set an end date'),
    every: Yup.string().required(),
    on_type: Yup.string().nullable(),
    on: Yup.mixed().nullable(),
});

export const RecurrenceManagementContext = React.createContext();

export const useRecurrenceManagementForm = () => {
    const context = useContext(RecurrenceManagementContext);
    if (context === undefined) {
        throw new Error('useRecurrenceManagementForm hook must be used within a RecurrenceManagementFormProvider');
    }
    return context;
};

const RecurrenceManagementFormProvider = ({children, booking, bookingConfig, onBookingFormChange}) => {

    const [localRecurrence, setLocalRecurrence] = useState();
    const [localRescheduledOccurrences, setLocalRescheduledOccurrences] = useState(booking?.rescheduled_occurrences);
    const [recurrenceErrors, setRecurrenceErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [calculatedOccurrences, setCalculatedOccurences] = useState([]);

    const [invalidCount, setInvalidCount] = useState();
    const [validCount, setValidCount] = useState();
    const [rescheduledCount, setRescheduledCount] = useState();
    const [toBeRescheduledCount, setToBeRescheduledCount] = useState();

    // Prevent user from seeing validation errors until they've had the first
    // successful occurrences response.
    const [showErrorsToUser, setShowErrorsToUser] = useState();

    /* Refresh local working copy of recurrence whenever the booking changes */
    useEffect(() => {
        let newRecurrence = (
            booking.recurrence
            && booking.recurrence.hasOwnProperty('repeats')
            && booking.recurrence.repeats
            && booking.recurrence.repeats.length > 1
        )
            ? booking.recurrence
            : recurrencePrototype

        // Ensure that the localEecurrence data object always has
        // start/time matching the booking
        if (booking.start) {
            newRecurrence.start_date = getDateFromDateString(booking.start);
            newRecurrence.time = getTimeFromDateString(booking.start);
            let interval = intervalFromState(booking);
            newRecurrence.duration = differenceInMinutes(interval.end, interval.start);
        }

        setLocalRecurrence(newRecurrence);
    }, [booking])

    /* Validate recurrence whenever it changes, and refresh occurrences & reschedules */
    useEffect(() => {
        // TODO - maybe don't wipe the reschedules if we're just changing end date (possibly other situations)
        setLocalRescheduledOccurrences({});
        setCalculatedOccurences([]);

        validateAndExpandRecurrence();
    }, [localRecurrence])

    /** Update counts whenever occurrences are calculated or resheduled */
    useEffect(() => {
        const invalidCount = calculatedOccurrences.filter((item) => item.is_available === false).length;
        const validCount = calculatedOccurrences.length - invalidCount;
        const rescheduledCount = (localRescheduledOccurrences)
            ? Object.keys(localRescheduledOccurrences).length
            : 0;
        const toBeRescheduledCount = invalidCount - rescheduledCount;

        setInvalidCount(invalidCount);
        setValidCount(validCount);
        setRescheduledCount(rescheduledCount);
        setToBeRescheduledCount(toBeRescheduledCount);
    }, [calculatedOccurrences, localRescheduledOccurrences])

    const validateAndExpandRecurrence = async() => {
        const validator = recurrenceValidationSchema;
        await validator
            .validate(localRecurrence, {abortEarly: false})
            .then(() => {
                setShowErrorsToUser(true);
                requestCalculatedOccurrences();
                setRecurrenceErrors({})
            })
            .catch((err) => {
                const theseErrors = err.inner.reduce((acc, error) => {
                    return {
                        ...acc,
                        [error.path]: error.message
                    }
                }, {})
                if (showErrorsToUser) {
                    setRecurrenceErrors(theseErrors);
                }
            })
    }

    const requestCalculatedOccurrences = async() => {
        setIsLoading(true);
        const params = {
            recurrence: localRecurrence,
            booking: {
                category_id: booking.category_id,
                amenity_id: booking.amenity_id,
                unit_id: booking.unit_id
            }
        };

        const response = await axios
            .post(`${BOOKINGS_API_URL}availability/calculate-occurrences`, params)
            .then(result => {
                const values = Object.values(result.data.data);
                setCalculatedOccurences(values);
                setRecurrenceErrors({});
            })
            .catch(function(error) {
                if (error.response?.data?.errors) {
                    const recurrenceErrors = getErrorsFor(error.response.data.errors, 'recurrence.', true)
                    setRecurrenceErrors(recurrenceErrors);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const handleRecurrenceChange = (updatedValues) => {
        const newRecurrence = {
            ...localRecurrence,
            ...updatedValues
        };
        setLocalRecurrence(newRecurrence);
    }

    const handleRescheduleChange = (occurrenceKey, rescheduledOccurrence) => {
        setLocalRescheduledOccurrences({
            ...localRescheduledOccurrences,
            [occurrenceKey]: rescheduledOccurrence
        });
    }

    const handleRemove = () => {
        setShowErrorsToUser(false);
        onBookingFormChange({
            recurrence: {},
            resheduled_occurrences: {}
        });
    }

    const handleConfirm = () => {
        const finalRescheduledRecurrences = skipRemainingInvalidOccurrences();
        setLocalRescheduledOccurrences(finalRescheduledRecurrences);
        setShowErrorsToUser(true);
        onBookingFormChange({
            recurrence: localRecurrence,
            rescheduled_occurrences: finalRescheduledRecurrences
        });
    }

    /**
     * If there are any invalid occurrences which have not been manually
     * rescheduled, just set them all to be skipped as if the user had done it
     * manually
     */
    const skipRemainingInvalidOccurrences = () => {
        const rescheduledOccurrenceKeys = Object.keys(localRescheduledOccurrences);
        const remainingInvalidOccurrences = calculatedOccurrences.filter(
            (item) => (item.is_available === false && !rescheduledOccurrenceKeys.includes(item.start))
        );

        if (!remainingInvalidOccurrences.length) {
            return localRescheduledOccurrences;
        }

        let skippedOccurrences = {};

        Object.values(remainingInvalidOccurrences).forEach((item) => {
            skippedOccurrences[item.start] = false;
        });

        return {
            ...localRescheduledOccurrences,
            ...skippedOccurrences
        }
    }

    return (
        <RecurrenceManagementContext.Provider
            value={{
                booking,
                bookingConfig,
                recurrence: localRecurrence,
                recurrenceErrors,
                calculatedOccurrences,
                rescheduledOccurrences: localRescheduledOccurrences,
                onRecurrenceChange: handleRecurrenceChange,
                onRescheduleChange: handleRescheduleChange,
                onConfirm: handleConfirm,
                onRemove: handleRemove,
                isLoading,
                validCount,
                invalidCount,
                rescheduledCount,
                toBeRescheduledCount
            }}
        >
            {children}
        </RecurrenceManagementContext.Provider>
    );
};

RecurrenceManagementFormProvider.propTypes = {

};

RecurrenceManagementFormProvider.defaultProps = {

};

export default RecurrenceManagementFormProvider;
